import React from 'react';
import { motion } from 'framer-motion';
import { PRO_STORAGE_GB, PRO_STORAGE_HOURS_320KBPS } from '../config/plans';
import HeroUseCaseTypewriter from './HeroUseCaseTypewriter';

// Landing hero — disciplined-base pass.
// No gradients in the first viewport, no blur blobs, no per-tier color chips.
// Type carries the weight; the single accent in viewport 1 lives in the header
// (the soundwave-500 Sign in button), so this card stays monochrome on purpose.
const tiers = [
  {
    name: 'Anonymous',
    headline: 'Just drop & share',
    bullets: ['7-day links', '50 MB per file', '128 kbps MP3', 'No signup needed'],
  },
  {
    name: 'Free account',
    headline: 'Library + creative tools',
    bullets: ['14-day links', 'Collections + recorder', '128 kbps MP3', 'Free signup'],
  },
  {
    name: 'Pro',
    headline: 'Links that never expire',
    bullets: [
      `Permanent storage (${PRO_STORAGE_GB} GB, ~${PRO_STORAGE_HOURS_320KBPS} hours of audio)`,
      '1.5 GB per file',
      '320 kbps MP3',
      '$5/month',
    ],
  },
];

const Hero: React.FC = () => {
  return (
    <motion.div
      className="w-full rounded-2xl border p-6 md:p-10"
      style={{
        backgroundColor: 'var(--background-surface)',
        borderColor: 'var(--border-color)',
      }}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center max-w-3xl mx-auto">
        <HeroUseCaseTypewriter />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-8 max-w-4xl mx-auto">
        {tiers.map((tier, i) => (
          <motion.div
            key={tier.name}
            className="rounded-2xl border p-4"
            style={{
              backgroundColor: 'rgba(255,255,255,0.02)',
              borderColor: 'rgba(255,255,255,0.08)',
            }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
          >
            <div className="mb-3">
              <p
                className="text-xs uppercase tracking-wide mb-0.5"
                style={{ color: 'var(--text-tertiary)' }}
              >
                {tier.name}
              </p>
              <p
                className="text-sm font-semibold leading-tight"
                style={{ color: 'var(--text-primary)' }}
              >
                {tier.headline}
              </p>
            </div>
            <ul className="space-y-1">
              {tier.bullets.map((b) => (
                <li
                  key={b}
                  className="text-xs flex items-start gap-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <span
                    className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0"
                    style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default Hero;
